/**
 * @architecture-header 1
 * @layer app
 * @domain workspace
 * @responsibility Define host-independent workspace and organization-student identity entry and recovery routes.
 * @state-owner React Router path and local stampless-session recovery state; the navigation store owns in-product movement
 * @allowed-imports routes and auth public APIs; app session provider; React Router
 * @forbidden browser history implementation; Electron history; module internals
 * @public-api createWorkspaceRoutes via @vispo/workspace-web
 * @side-effects route rendering, authenticated sign-out, and recovery navigation to a portal
 * @tests workspace Playwright flows in frontend/apps/web/e2e/workspace.spec.ts
 */

import { useEffect, useRef, useState } from 'react';
import { Navigate, type RouteObject, useNavigate } from 'react-router-dom';
import {
  consumeSignedOutAudience,
  type OrganizationSession,
  OrganizationStudentAuthScreen,
  SignInScreen,
} from '../../modules/auth';
import { WorkspaceShell } from '../../routes/WorkspaceShell';
import { VispoBrandLogo } from '../../shared/components/BrandMarks';
import { VispoLoading } from '../../shared/components/WorkspaceState';
import { useWorkspaceEnvironment } from '../config/environment';
import { agentARoutes } from '../feature-composition/agent-a/Routes';
import { agentBRoutes } from '../feature-composition/agent-b/Routes';
import { useWorkspaceSession } from '../providers/SessionProvider';

const ORGANIZATION_LEGAL_VERSION = {
  consentRequired: true,
  privacyVersion: 'privacy-v1',
  termsVersion: 'terms-v1',
} as const;

function OrganizationIdentityEntry({ mode }: { mode: 'login' | 'activate' }) {
  const navigate = useNavigate();
  const { acceptOrganizationSession, isLoading, session } = useWorkspaceSession();

  if (isLoading) return <VispoLoading className="workspace-loading" label="正在確認登入狀態…" />;
  if (session) return <Navigate replace to="/" />;

  const completeAuthentication = async (organizationSession: OrganizationSession) => {
    acceptOrganizationSession(organizationSession);
    await navigate('/student/workspace', { replace: true });
  };

  return (
    <OrganizationStudentAuthScreen
      initialMode={mode}
      legal={ORGANIZATION_LEGAL_VERSION}
      onAuthenticated={completeAuthentication}
    />
  );
}

function AudienceIdentityEntry({ audience }: { audience: 'student' | 'teacher' }) {
  const { auth, isLoading, refresh, session } = useWorkspaceSession();
  const { demoMode, enforcePortalAudience } = useWorkspaceEnvironment();
  const [portalStampFailed, setPortalStampFailed] = useState(false);

  useEffect(() => {
    if (!session || session.portalAudience || !auth.setPortalAudience) return;
    let active = true;
    void auth
      .setPortalAudience(audience)
      .then(async () => {
        if (active) await refresh();
      })
      .catch(() => {
        if (active) setPortalStampFailed(true);
      });
    return () => {
      active = false;
    };
  }, [audience, auth, refresh, session]);

  if (isLoading) return <VispoLoading className="workspace-loading" label="正在確認登入狀態…" />;
  if (session) {
    if (!session.portalAudience && (!demoMode || enforcePortalAudience)) {
      if (auth.setPortalAudience && !portalStampFailed) {
        return <VispoLoading className="workspace-loading" label="正在準備登入入口…" />;
      }
      return <AudienceSessionExpired />;
    }
    return <Navigate replace to={`/${session.portalAudience ?? audience}/workspace`} />;
  }
  return <SignInScreen audience={audience} />;
}

function AudienceSessionExpired() {
  const navigate = useNavigate();
  const { signOut } = useWorkspaceSession();
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const restart = async () => {
    setBusy(true);
    setError(null);
    try {
      await signOut();
      await navigate('/login', { replace: true });
    } catch {
      setError('無法結束目前登入，請再試一次。');
      setBusy(false);
    }
  };

  return (
    <main className="workspace-error" role="alert">
      <VispoBrandLogo />
      <h1>請重新登入</h1>
      <p>為保護你的工作區，請從原本的登入入口重新登入。</p>
      {error ? <p>{error}</p> : null}
      <button className="bt pri" disabled={busy} type="button" onClick={() => void restart()}>
        {busy ? '正在登出…' : '登出並重新登入'}
      </button>
    </main>
  );
}

function NeutralLoginEntry() {
  // A signed-out portal audience returns to its own login page; only a no-stamp direct
  // visit (or stampless recovery) falls through to the neutral chooser below.
  // consumeSignedOutAudience() has a one-shot side effect, so a ref guards it against
  // StrictMode's double render — both passes must resolve to the same captured value.
  const consumed = useRef(false);
  const capturedAudience = useRef<'student' | 'teacher' | null>(null);
  if (!consumed.current) {
    consumed.current = true;
    capturedAudience.current = consumeSignedOutAudience();
  }
  if (capturedAudience.current) {
    return <Navigate replace to={`/${capturedAudience.current}/login`} />;
  }
  return (
    <main className="workspace-error">
      <VispoBrandLogo />
      <h1>選擇登入入口</h1>
      <p>請選擇你要使用的 Vispo 工作區入口。</p>
      <div>
        <a className="bt pri" href="/student/login">
          學生登入
        </a>
        <a className="bt" href="/teacher/login">
          老師登入
        </a>
      </div>
    </main>
  );
}

/**
 * The  product is a single shell whose section, selection and overlays live in the
 * navigation store, not the URL. The paths below only seed the entry
 * section so deep links keep working.
 */
export function createWorkspaceRoutes(): RouteObject[] {
  return [
    { path: '/login', element: <NeutralLoginEntry /> },
    { path: '/student', element: <Navigate replace to="/student/login" /> },
    { path: '/student/login', element: <AudienceIdentityEntry audience="student" /> },
    { path: '/', element: <Navigate replace to="/student/login" /> },
    { path: '/student/org-login', element: <OrganizationIdentityEntry mode="login" /> },
    { path: '/student/org-activate', element: <OrganizationIdentityEntry mode="activate" /> },
    { path: '/teacher', element: <Navigate replace to="/teacher/login" /> },
    { path: '/teacher/login', element: <AudienceIdentityEntry audience="teacher" /> },
    { path: '/student/workspace', element: <WorkspaceShell desiredRole="student" /> },
    { path: '/student/activities', element: <WorkspaceShell desiredRole="student" /> },
    { path: '/student/classes', element: <WorkspaceShell desiredRole="student" /> },
    { path: '/student/reports', element: <WorkspaceShell desiredRole="student" /> },
    { path: '/teacher/workspace', element: <WorkspaceShell desiredRole="teacher" /> },
    { path: '/teacher/activities', element: <WorkspaceShell desiredRole="teacher" /> },
    { path: '/teacher/classes', element: <WorkspaceShell desiredRole="teacher" /> },
    { path: '/teacher/live', element: <WorkspaceShell desiredRole="teacher" /> },
    { path: '/teacher/reports', element: <WorkspaceShell desiredRole="teacher" /> },
    { path: '/teacher/settings', element: <WorkspaceShell desiredRole="teacher" /> },
    { path: '/teacher/settings/:tab', element: <WorkspaceShell desiredRole="teacher" /> },
    { path: '/activities', element: <WorkspaceShell desiredRole="teacher" /> },
    { path: '/classes', element: <WorkspaceShell desiredRole="teacher" /> },
    { path: '/live', element: <WorkspaceShell desiredRole="teacher" /> },
    { path: '/reports', element: <WorkspaceShell desiredRole="teacher" /> },
    ...agentARoutes,
    ...agentBRoutes,
    { path: '*', element: <Navigate replace to="/student/login" /> },
  ].map((route) => ({ ...route, errorElement: <WorkspaceRouteError /> }));
}

export function WorkspaceRouteError() {
  return (
    <main className="workspace-error" role="alert">
      <VispoBrandLogo />
      <h1>畫面暫時無法載入</h1>
      <p>你的登入狀態還在。請先重新載入；如果仍無法使用，再回到登入頁。</p>
      <div>
        <button className="bt pri" type="button" onClick={() => window.location.reload()}>
          重新載入
        </button>
        <a className="bt" href="/student/login">
          回到登入頁
        </a>
      </div>
    </main>
  );
}
